/**
 * The gestures (M4-07) — what the mute have instead of words.
 *
 * Built to the aim menu's pattern, like every other menu here: **one key to
 * open, one key to choose, and it is done**. Three signs, and deliberately
 * three: *there*, *come*, and *danger*. Everything past that a party invents
 * for itself, which is the point — a silent player and four people working out
 * what their hands mean is the story the mute mark exists to produce.
 *
 * Pure state. It knows nothing about the DOM and nothing about the socket.
 */

export const GESTURE_KEYS: readonly { readonly key: string; readonly gesture: ClientGesture }[] = [
  { key: 'p', gesture: 'point' },
  { key: 'b', gesture: 'beckon' },
  { key: 'w', gesture: 'warn' },
];

export type ClientGesture = 'point' | 'beckon' | 'warn';

const GESTURE_OF_KEY = new Map(GESTURE_KEYS.map((entry) => [entry.key, entry.gesture]));

/** The word the prompt shows. Plain verbs, like every other menu's. */
const LABEL: { readonly [G in ClientGesture]: string } = {
  point: 'point',
  beckon: 'beckon',
  warn: 'warn',
};

/** `point (p) · beckon (b) · warn (w)`. */
export function gesturePrompt(): string {
  return GESTURE_KEYS.map(({ key, gesture }) => `${LABEL[gesture]} (${key})`).join(' · ');
}

export class GestureMenu {
  private open = false;

  get active(): boolean {
    return this.open;
  }

  show(): string {
    this.open = true;
    return gesturePrompt();
  }

  close(): void {
    this.open = false;
  }

  /**
   * Feed it a key. Returns the gesture to send, or `null`.
   *
   * Closes on anything, mistyped keys included — one press resolves the menu
   * either way, exactly as the aim menu does, because a menu you have to escape
   * out of is a menu that costs two keystrokes.
   */
  choose(key: string): ClientGesture | null {
    if (!this.open) return null;
    this.close();
    return GESTURE_OF_KEY.get(key.toLowerCase()) ?? null;
  }
}
